/**
 * @voltxampere/engine — AC frequency sweep generator (Sprint 20)
 *
 * Three sweep distributions, matching ngspice's `.AC` directive:
 *
 *  - `"dec"`: logarithmic by decade. `points` total samples
 *    distributed uniformly in `log10` space across [fStart, fStop].
 *    Most common AC analysis form — Bode plots are read on log axes.
 *  - `"oct"`: logarithmic by octave (log2 space).
 *  - `"lin"`: linear distribution.
 *
 * Caller convention: `points` is the *total* number of samples (not
 * "points per decade"). Ngspice's syntax `dec NPTS FSTART FSTOP`
 * uses points-per-decade; the codec layer (Sprint 20 commit C7)
 * converts that to total points before handing config to this
 * function.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Generate the frequency list. Returns a fresh `Float64Array` of
 * length `config.points`. The first sample is `fStart`, the last
 * is `fStop`; intermediate samples are evenly spaced in the chosen
 * domain.
 */
export function sweepFrequencies(config) {
    const { fStart, fStop, points, sweep } = config;
    if (!Number.isFinite(fStart) || !Number.isFinite(fStop)) {
        throw new ContractViolationError(`sweepFrequencies: fStart and fStop must be finite (got ${String(fStart)}, ${String(fStop)})`);
    }
    if (fStart <= 0) {
        throw new ContractViolationError(`sweepFrequencies: fStart must be > 0 (got ${String(fStart)})`);
    }
    if (fStop <= fStart) {
        throw new ContractViolationError(`sweepFrequencies: fStop (${String(fStop)}) must be > fStart (${String(fStart)})`);
    }
    if (!Number.isInteger(points) || points < 2) {
        throw new ContractViolationError(`sweepFrequencies: points must be an integer ≥ 2 (got ${String(points)})`);
    }
    const out = new Float64Array(points);
    if (sweep === "lin") {
        const step = (fStop - fStart) / (points - 1);
        for (let i = 0; i < points; i++)
            out[i] = fStart + step * i;
        return out;
    }
    // Logarithmic — pick the base.
    const base = sweep === "dec" ? 10 : 2;
    const logFn = sweep === "dec" ? Math.log10 : Math.log2;
    const logStart = logFn(fStart);
    const logStop = logFn(fStop);
    const step = (logStop - logStart) / (points - 1);
    for (let i = 0; i < points; i++) {
        out[i] = Math.pow(base, logStart + step * i);
    }
    return out;
}
//# sourceMappingURL=sweep.js.map